/**
 * The score for all the shareholders together, in dollars (OR-30.4): the score per share held at the start times the shares at the start, so a point has a size ("about $36M a point").
 * Both factors are the report's own: `valueCreatedUsdPerShare` and `valueCreatedBaseUsdPerShare` (one point is 1% of it) per SimStart share, and `score.components.shares.simStartShares`.
 * Null when the report carries no share count (results stored before ADR-0059).
 */
import type { RoundReport } from "@bsim/contracts";
import { formatMetric } from "@gxs/metric-defs";
import { formatUsdPerShare, formatUsdScaled } from "../format";

/** `baseUsdPerShare` and `shares` are the two factors of a point's size, kept so a screen can name them. */
export interface ShareholderTotal { readonly scoreUsd: number; readonly perPointUsd: number; readonly baseUsdPerShare: number; readonly shares: number }

export function shareholderTotal(report: RoundReport): ShareholderTotal | null {
  const shares = report.score.components?.shares?.simStartShares;
  const base = report.scorecard.valueCreatedBaseUsdPerShare;
  if (shares === undefined || shares <= 0 || base <= 0) return null;
  // The score is the cent figure the page prints beside it. A point's size is built from the unrounded base and rounded once, at display, so the printed base is the rounded view of the number the rate came from.
  const cents = (v: number): number => Math.round(v * 100) / 100;
  return { scoreUsd: cents(report.score.valueCreatedUsdPerShare) * shares, perPointUsd: (base / 100) * shares, baseUsdPerShare: base, shares };
}

/** "about $36.4M for all your shareholders together" (the score), or null without a share count. */
export function shareholderTotalWords(report: RoundReport, signed = false): string | null {
  const t = shareholderTotal(report);
  return t === null ? null : `${signed ? formatUsdScaled(t.scoreUsd, { signed: true }) : formatUsdScaled(Math.abs(t.scoreUsd))} for all your shareholders together`;
}

/**
 * The one definition of a point's size (chapter 10.4): a point is 1% of the value base per share, which is your twin's value per share in today's dollars and moves with the scenario and the year;
 * for all your shareholders it is that times the shares at the start. Every screen that prints a dollar total or a dollars-a-point figure says it with this sentence, so the two always agree.
 * "each point is about $30.6M: 1% of your twin's value per share ($59.72, in today's dollars) times the 51.25M shares at the start." Null without a share count.
 */
export function perPointWords(report: RoundReport): string | null {
  const t = shareholderTotal(report);
  return t === null ? null : pointSizeWords(t.baseUsdPerShare, t.shares);
}

/**
 * The sentence itself, for a screen with no report yet (the solo intro): `about` marks a base that is only the twin's start price (the exact base follows the scenario); without a share count the total is left out and the shares are named, not counted.
 * Text before the first report may not name a figure it does not have.
 */
export function pointSizeWords(baseUsdPerShare: number, shares: number | null, about = false): string {
  const base = `${about ? "about " : ""}${formatUsdPerShare(baseUsdPerShare)}, in today's dollars`;
  return shares === null || shares <= 0
    ? `each point is 1% of your twin's value per share (${base}) times the shares at the start`
    : `each point is about ${formatUsdScaled((baseUsdPerShare / 100) * shares)}: 1% of your twin's value per share (${base}) times the ${formatMetric(shares / 1e6, "index", { precision: 2 })}M shares at the start`;
}
